import { InfoCircleFilled } from '@ant-design/icons'
import { Tooltip } from 'antd'
import { useAccessibility } from 'context/hooks/useAccessibility'
import { useFamily } from 'context/hooks/useFamily'
import useQuery from 'context/hooks/useQuery'
import { useEffect, useMemo, useState } from 'react'
import { getOverlayCustomTooltip } from 'utils/utils'

import './styles.scss'

interface CardReliabilityProps {
  indicative: number | undefined | null // indicates the current similarity metric
  colors?: string[] // colors that will be used, is optional
  threshold?: number[] // 2 number Array for color bounds, it's optional
  isHighContrastAndDefaultColors?: boolean
}

function CardReliability({
  indicative,
  colors,
  threshold,
  isHighContrastAndDefaultColors = false,
}: CardReliabilityProps) {
  const [countPercentage, setCountPercentage] = useState<number>(0)

  const { contrastIsSelected } = useAccessibility()

  const { families } = useFamily()

  const { basesNames } = useQuery()

  function handleBaseWithInformation() {
    const baseWithInformation =
      basesNames[families.find((family) => family.family)?.base ?? ''] // busca a unica base que tem informações da familia, e pega o nome dela no basesNames[keyDaBase]

    return (
      <>
        na base <strong>{baseWithInformation}</strong>
      </>
    )
  }

  const colorsDefault = colors ?? [
    '#489E34',
    !isHighContrastAndDefaultColors ? '#E9CA3B' : '#808080',
    '#F67D7D',
    '#E6E6E6',
    '#F67D7D',
  ]

  const thresholdDefault = threshold ?? [100, 50]

  const currentColorBackground = useMemo(() => {
    if (indicative === undefined) return colorsDefault[3]
    else if (indicative === null) return colorsDefault[4]
    else if (countPercentage === thresholdDefault[0]) return colorsDefault[0]
    else if (
      countPercentage < thresholdDefault[0] &&
      countPercentage > thresholdDefault[1]
    )
      return colorsDefault[1]
    return colorsDefault[2]
  }, [indicative, countPercentage, thresholdDefault])

  const titleInfo = useMemo(() => {
    if (indicative === undefined)
      return (
        <>
          A família selecionada está presente apenas{' '}
          {handleBaseWithInformation()}, por isso não é possível calcular a
          consistência entre bases
        </>
      )
    else if (indicative === null)
      return 'Ocorreu um erro ao calcular a porcentagem de informações consistentes entre as bases'
    else if (indicative === thresholdDefault[0])
      return 'As informações dessa família são 100% consistentes entre as bases'
    else if (indicative <= thresholdDefault[1])
      return `As informações dessa família são ${indicative}% consistentes entre as bases`
    return `As informações dessa família são ${indicative}% consistentes entre as bases`
  }, [indicative, thresholdDefault])

  const progressValue = useMemo(() => {
    if (indicative === undefined)
      return <h1 style={{ fontSize: '1.25rem' }}>Consistência indisponível</h1>
    else if (indicative === null) return <h1>Erro no cálculo</h1>
    return (
      <>
        <h1>
          {' '}
          <span className="progress-value">{countPercentage}</span>%
        </h1>
        <p>consistente</p>
      </>
    )
  }, [indicative, countPercentage])

  const titleInfoTooltip = (
    <span className="reliability-tooltip-content">{titleInfo}</span>
  )

  useEffect(() => {
    const isNullOrUndefined = indicative === undefined || indicative === null
    const indicativePercentage = isNullOrUndefined ? 100 : indicative
    let percentage = 0

    const id = setInterval(() => {
      percentage = !isNullOrUndefined ? percentage + 1 : 100
      setCountPercentage(percentage)
      if (percentage === indicativePercentage) {
        clearInterval(id)
      }
    }, 10)

    return () => {
      clearInterval(id)
    }
  }, [])

  const styleProgressBar = `conic-gradient(${currentColorBackground} ${countPercentage}%, #E6E6E6 ${countPercentage}%)`

  return (
    <Tooltip
      overlayClassName="cardReliability__tooltip"
      title={titleInfoTooltip}
      placement="left"
      arrowPointAtCenter
      overlayStyle={getOverlayCustomTooltip(contrastIsSelected)}
      color={contrastIsSelected ? 'var(--contrast-background-color)' : ''}
    >
      <div className="cardReliability-pie-radial-container">
        <div
          className="circular-progress"
          style={{
            background: styleProgressBar,
          }}
        >
          {progressValue}
        </div>
        <div className="container-cardReliability__info">
          <InfoCircleFilled
            color={`var(--green-dark)`}
            style={{ color: 'var(--green-dark)', fontSize: 20 }}
          />
        </div>
      </div>
    </Tooltip>
  )
}

export default CardReliability
